import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCreateDriverLead,
  useGetDriverLeadDetails,
  useUpdateDriverLead,
} from "../../../hooks/use-driver";
import { uploadCibilDocuments } from "../utils/uploadCibilDocuments";
import { useAuth } from "../../../contexts/AuthContext";
import { getErrorMessage } from "../../../utils/getErrorMessage";
import { useEmiAdminConfig } from "../../../hooks/useEmiAdminConfig";
import { getEligibleFinanciers } from "../config/emiConfig";
import { ROLES } from "../../../constants/roles";
import paths from "../../../routes/paths";
import { validateStepData } from "../utils/leadFormSchemas";
import {
  stripUndefined,
  buildInitialFormData,
  getLeadIdFromUrl,
  isEqual,
  getInitialStepFromLead,
  mapLeadToFormData,
  hasNewDocuments,
} from "../utils/leadDataHelpers";
import { registerDriverFromLead } from "../utils/registerDriverFromLead";
import { STEPS, validateDocumentUploadByRole } from "../config/stepsConfig";

export const useLeadCreationForm = () => {
  const { user, getUserRole } = useAuth();
  const role = getUserRole();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlLeadId = getLeadIdFromUrl(searchParams);

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(buildInitialFormData);
  const [leadData, setLeadData] = useState(null);
  const [savedFormData, setSavedFormData] = useState(null);
  const [showErrors, setShowErrors] = useState(false);
  const [leadId, setLeadId] = useState(urlLeadId);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [stepErrors, setStepErrors] = useState({});
  const [phoneNumberTaken, setPhoneNumberTaken] = useState(false);
  const [phoneCheckStatus, setPhoneCheckStatus] = useState("idle");
  const [isInitializing, setIsInitializing] = useState(Boolean(urlLeadId));
  const [isSaving, setIsSaving] = useState(false);

  const {
    mutateAsync: createDriverLead,
    isPending: isCreatingLead,
    isError,
    error,
  } = useCreateDriverLead();

  const { mutateAsync: updateDriverLead, isPending: isUpdatingingLead } =
    useUpdateDriverLead();

  const { data: emiAdminConfig } = useEmiAdminConfig("v2");

  const {
    data: leadResponse,
    isLoading: isLoadingLead,
    isError: isLeadFetchError,
    error: leadFetchError,
    refetch: refetchLead,
  } = useGetDriverLeadDetails(leadId);

  useEffect(() => {
    if (!urlLeadId) {
      if (leadId !== null) {
        setLeadId(null);
        setFormData(buildInitialFormData());
        setSavedFormData(null);
        setActiveStep(0);
        setSavedFormData(null);
        setShowErrors(false);
        setUploadError(null);
      }

      setIsInitializing(false);
      return;
    }

    if (isLoadingLead) {
      return;
    }

    if (leadResponse) {
      const loadedFormData = mapLeadToFormData(leadResponse);

      const initialStep = getInitialStepFromLead(leadResponse);

      if (initialStep !== null) {
        setActiveStep(initialStep);
      }

      setLeadId(leadResponse.leadId || urlLeadId);
      setFormData(loadedFormData);
      setSavedFormData(loadedFormData);
      setLeadData(leadResponse);
    }

    setIsInitializing(false);
  }, [urlLeadId, leadResponse, isLoadingLead]);

  const currentStep = STEPS[activeStep];
  const currentStepData = formData[currentStep.key];
  const isCibilNumberEntered = Boolean(formData.documentUpload?.cibilNumber);

  const isStepValid = useMemo(() => {
    if (currentStep.key === "documentUpload") {
      return validateDocumentUploadByRole(currentStepData, role);
    }
    return currentStep.validate(currentStepData);
  }, [currentStep, currentStepData, role]);

  const isLastStep = activeStep === STEPS.length - 1;

  const hasStepChanged = (stepKey) => {
    if (!savedFormData) return true;
    const currentStepValue = formData[stepKey];
    const savedStepValue =
      savedFormData[stepKey] ??
      STEPS.find((s) => s.key === stepKey)?.initialData;
    return !isEqual(currentStepValue, savedStepValue);
  };

  const hasFormChanged = useMemo(
    () => hasStepChanged(currentStep.key),
    [formData, savedFormData, currentStep.key],
  );

  const updateStepData = (field, value) => {
    const prevValue = formData[currentStep.key]?.[field];

    setFormData((prev) => ({
      ...prev,
      [currentStep.key]: {
        ...prev[currentStep.key],
        [field]: value,
      },
    }));

    clearFieldError(field);

    if (value && typeof value === "object" && !Array.isArray(value)) {
      Object.keys(value).forEach((key) => {
        if (value[key] !== prevValue?.[key]) {
          clearFieldError(`${field}.${key}`);
        }
      });
    }
  };

  const clearFieldError = (field) => {
    setStepErrors((prev) => {
      if (!prev[field]) return prev;
      const updated = { ...prev };
      delete updated[field];
      return updated;
    });
  };

  const updateSavedFormData = (partial) => {
    setSavedFormData((prev) => ({
      ...(prev || buildInitialFormData()),
      ...partial,
    }));
  };

  const saveLead = async (payload) => {
    let response;
    const isFinancierChangeRequest =
      leadData?.isFinancierChangeRequest || false;

    if (isFinancierChangeRequest) {
      response = await updateDriverLead({ leadId: payload.leadId, payload });
    } else {
      response = await createDriverLead(stripUndefined(payload));
    }

    const returnedId =
      response?.leadId ||
      response?.id ||
      response?.data?.leadId ||
      response?.data?.id;

    if (returnedId) {
      setLeadId(returnedId);
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("leadId", returnedId);
        return params;
      });
    }

    return { response, leadId: returnedId };
  };

  const saveDriverInformation = async () => {
    if (!hasFormChanged && leadId) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    await saveLead({
      leadId: leadId || undefined,
      driverInformation: formData.driverInformation,
    });

    updateSavedFormData({ driverInformation: formData.driverInformation });
    setActiveStep((prev) => prev + 1);
  };

  const saveAssetAllocation = async () => {
    if (!hasFormChanged && leadId) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    await saveLead({
      leadId,
      driverInformation: formData.driverInformation,
      assetAllocation: formData.assetAllocation,
    });

    updateSavedFormData({ assetAllocation: formData.assetAllocation });
    setActiveStep((prev) => prev + 1);
  };

  const saveDocumentUpload = async () => {
    const savedDocs = savedFormData?.documentUpload;
    const docsChanged = !isEqual(
      formData.documentUpload,
      savedDocs || STEPS[2].initialData,
    );

    if (!docsChanged && leadId) {
      setActiveStep((prev) => prev + 1);
      return;
    }

    let documentPayload = formData.documentUpload;

    if (
      role === ROLES.DEALER &&
      hasNewDocuments(formData.documentUpload, savedDocs)
    ) {
      setIsUploading(true);
      try {
        documentPayload = await uploadCibilDocuments(
          formData.documentUpload,
          leadId,
        );
      } catch (err) {
        setUploadError(getErrorMessage(err, "Failed to upload documents"));
        throw err;
      } finally {
        setIsUploading(false);
      }
    }

    if (role === ROLES.ADMIN) {
      documentPayload = {
        ...documentPayload,
        enteredBy: user?.email || "",
        enteredAt: new Date().toISOString(),
      };
    }

    await saveLead({
      leadId,
      driverInformation: formData.driverInformation,
      documentUpload: documentPayload,
    });

    setFormData((prev) => ({
      ...prev,
      documentUpload: { ...prev.documentUpload, ...documentPayload },
    }));
    updateSavedFormData({ documentUpload: documentPayload });
    setActiveStep((prev) => prev + 1);
  };

  const saveFinancierSelection = async (selectedFinancier) => {
    const isFinancierChangeRequest =
      leadData?.isFinancierChangeRequest || false;

    if (isFinancierChangeRequest) {
      await saveLead({
        leadId,
        loanSummary: selectedFinancier,
      });
    } else {
      await saveLead({
        leadId,
        driverInformation: formData.driverInformation,
        documentUpload: formData.documentUpload,
        loanSummary: selectedFinancier,
      });
    }

    await refetchLead();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setUploadError(null);

    try {
      const result = await registerDriverFromLead(leadData);

      if (!result.success) {
        setUploadError(result.error);
        return;
      }

      navigate(`${paths.DRIVER_ONBOARDING}?tab=leads`);
    } catch (err) {
      setUploadError(
        getErrorMessage(
          err,
          "Something went wrong while registering the driver.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (isSaving) return;

    if (
      currentStep.key === "driverInformation" &&
      phoneCheckStatus === "checking"
    ) {
      return;
    }

    setIsSaving(true);

    const { isValid, errors } = await validateStepData(
      currentStep.key,
      currentStepData,
      role,
      {
        phoneNumberTaken:
          phoneCheckStatus === "taken" || phoneCheckStatus === "error",
      },
    );
    setStepErrors(errors);

    if (!isValid) {
      setShowErrors(true);
      setIsSaving(false);
      return;
    }

    setShowErrors(false);
    setUploadError(null);

    try {
      if (currentStep.key === "driverInformation") {
        await saveDriverInformation();
        return;
      }
      if (currentStep.key === "assetAllocation") {
        await saveAssetAllocation();
        return;
      }
      if (currentStep.key === "documentUpload") {
        await saveDocumentUpload();
        return;
      }
      if (currentStep.key === "financierSelection") {
        await saveFinancierSelection();
        return;
      }
      setActiveStep((prev) => prev + 1);
    } catch (err) {
      setUploadError(
        getErrorMessage(err, "Something went wrong while saving the lead."),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    setShowErrors(false);
    setUploadError(null);
    setStepErrors({});
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const isBusy = isUploading || isCreatingLead || isUpdatingingLead || isSaving;

  const eligibleFinanciers = getEligibleFinanciers(emiAdminConfig, {
    cibilScore: formData.documentUpload?.cibilNumber,
    downpayment: formData.driverInformation?.downpayment,
    city: formData.driverInformation?.city,
  });

  const isActionBtnDisabled =
    (currentStep.key === "documentUpload" &&
      role === ROLES.DEALER &&
      isCibilNumberEntered) ||
    (currentStep.key === "financierSelection" &&
      eligibleFinanciers.length === 0) ||
    (currentStep.key === "documentUpload" &&
      role === ROLES.ADMIN &&
      currentStepData.cibilNumber &&
      emiAdminConfig?.minCIBIL &&
      Number(currentStepData.cibilNumber) < Number(emiAdminConfig.minCIBIL));

  const isSaveBtnDisabled =
    (currentStep.key === "driverInformation" &&
      phoneCheckStatus === "checking") ||
    Object.keys(stepErrors).length > 0;

  const isSubmitBtnDisabled =
    !leadData?.loanSummary?.financier || leadData?.status !== "DRAFT";

  const nextButtonLabel =
    currentStep.key === "documentUpload" &&
    role === ROLES.DEALER &&
    !isCibilNumberEntered
      ? "Submit for CIBIL Review"
      : undefined;

  const leadEligibleFinanciers = leadData?.eligibleFinanciers;
  const loanSummary = leadData?.loanSummary;
  const loanSummaryExists = loanSummary && Object.keys(loanSummary)?.length > 0;
  const loadingLabel = isUploading ? "Uploading..." : "Saving...";

  const isBackDisabled =
    isUploading ||
    isCreatingLead ||
    isUpdatingingLead ||
    loanSummaryExists ||
    (leadEligibleFinanciers && leadEligibleFinanciers.length > 0);

  return {
    role,
    activeStep,
    formData,
    leadData,
    setLeadData,
    showErrors,
    leadId,
    stepErrors,
    setStepErrors,
    setPhoneCheckStatus,
    isInitializing,
    isLoadingLead,
    isLeadFetchError,
    leadFetchError,
    isError,
    error,
    uploadError,
    currentStep,
    currentStepData,
    isLastStep,
    isBusy,
    isSubmitting,
    isActionBtnDisabled,
    isSaveBtnDisabled,
    isSubmitBtnDisabled,
    nextButtonLabel,
    loadingLabel,
    isBackDisabled,
    updateStepData,
    handleNext,
    handleBack,
    handleSubmit,
    saveFinancierSelection,
    clearFieldError,
  };
};
